import React, { useEffect, useRef, useState } from 'react';
import { ChevronsUpDown, X } from 'lucide-react';
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@roger/ui-components';
import { WOUND_TYPES } from '../../constants/wounds';

interface WoundTypeComboboxProps {
  value: string;
  onValueChange: (value: string) => void;
  onAddCustomType?: (search: string) => void;
}

const highlightMatch = (text: string, query: string): React.ReactNode => {
  if (!query) return text;
  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const index = lowerText.indexOf(lowerQuery);
  if (index === -1) return text;
  return (
    <>
      {text.slice(0, index)}
      <span className='font-bold'>
        {text.slice(index, index + query.length)}
      </span>
      {text.slice(index + query.length)}
    </>
  );
};

const WoundTypeCombobox: React.FC<WoundTypeComboboxProps> = ({
  value,
  onValueChange,
  onAddCustomType,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const selectedLabel = WOUND_TYPES.find(t => t.value === value)?.label ?? '';

  const filtered = search
    ? WOUND_TYPES.filter(t =>
        t.label.toLowerCase().includes(search.toLowerCase())
      )
    : WOUND_TYPES;

  // When the popover opens, focus the input
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 0);
    } else {
      setSearch('');
    }
  }, [open]);

  const handleSelect = (val: string) => {
    onValueChange(val);
    setOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onValueChange('');
    setSearch('');
  };

  const handleListWheel = (e: React.WheelEvent<HTMLUListElement>) => {
    if (!listRef.current) return;
    listRef.current.scrollTop += e.deltaY;
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type='button'
          role='combobox'
          aria-expanded={open}
          className='flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors hover:bg-accent/30 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring'
        >
          <span className={value ? 'text-foreground' : 'text-muted-foreground'}>
            {value ? selectedLabel : 'Select type...'}
          </span>
          <span className='flex items-center gap-1 shrink-0'>
            {value && (
              <span
                role='button'
                tabIndex={0}
                className='rounded p-0.5 hover:bg-muted cursor-pointer'
                onClick={handleClear}
                onKeyDown={e =>
                  e.key === 'Enter' &&
                  handleClear(e as unknown as React.MouseEvent)
                }
              >
                <X className='h-3 w-3 text-muted-foreground' />
              </span>
            )}
            <ChevronsUpDown className='h-4 w-4 opacity-50' />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className='p-0 w-[var(--radix-popover-trigger-width)]'
        align='start'
        sideOffset={4}
        onOpenAutoFocus={e => e.preventDefault()}
      >
        <div className='border-b px-2 py-1.5'>
          <input
            ref={inputRef}
            type='text'
            placeholder='Filter types...'
            value={search}
            onChange={e => setSearch(e.target.value)}
            className='w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground'
          />
        </div>
        <ul
          ref={listRef}
          className='max-h-60 overflow-y-auto overflow-x-hidden overscroll-contain py-1'
          onWheel={handleListWheel}
        >
          {filtered.length === 0 ? (
            <li className='px-3 py-4 text-center space-y-2'>
              <p className='text-sm text-muted-foreground'>No types found</p>
              {onAddCustomType && (
                <Button
                  variant='outline'
                  size='sm'
                  className='mx-auto block'
                  onClick={() => {
                    onAddCustomType(search);
                    setOpen(false);
                  }}
                >
                  Add "{search}" as custom type
                </Button>
              )}
            </li>
          ) : (
            filtered.map(t => (
              <li key={t.value}>
                <Button
                  variant='ghost'
                  className='w-full justify-start px-3 py-1.5 h-auto text-sm font-normal rounded-none'
                  onClick={() => handleSelect(t.value)}
                >
                  <span className={value === t.value ? 'text-foreground' : ''}>
                    {highlightMatch(t.label, search)}
                  </span>
                </Button>
              </li>
            ))
          )}
        </ul>
      </PopoverContent>
    </Popover>
  );
};

export default WoundTypeCombobox;
